import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { MessageData } from '@/components/Bubble/types'
import getNetwatchUser from '@/lib/getNetwatchUser'

const MAX_MESSAGES = 50

export const useChatStore = defineStore('chat', () => {
  // Initialize with default welcome message, will be replaced by server data
  const defaultMessages: MessageData[] = [
    {
      isReply: undefined,
      messageContent:
        '👋 Hi there! I am here to help you monitor and manage your infrastructure. What would you like to know?',
      messageAvatar: undefined,
      aiInfo: true,
      isAfterMessage: false,
      isBeforeMessage: true,
      isLoadingMessage: false,
      time: new Date(),
    },
  ]

  const responses = ref<MessageData[]>(defaultMessages)
  const isLoaded = ref(false)

  // Fetch messages from server
  async function loadMessages(apiHost: string, clientId: string) {
    if (isLoaded.value)
      return

    try {
      const username = getNetwatchUser()
      const response = await fetch(`${apiHost}/messages`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-client-id': clientId,
          'x-username': username,
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.messages) {
          responses.value = data.messages.map((msg: any) => ({
            ...msg,
            time: new Date(msg.time),
          }))
        }
      }
      else {
        console.warn('Failed to load messages from server, using default')
      }
    }
    catch (error) {
      console.warn('Error loading messages from server:', error)
    }
    finally {
      isLoaded.value = true
    }
  }

  function updateResponses(response: MessageData) {
    responses.value.push(response)
    // Keep only the last MAX_MESSAGES messages
    if (responses.value.length > MAX_MESSAGES) {
      responses.value = responses.value.slice(-MAX_MESSAGES)
    }
  }

  function deleteLastResponse(content: string) {
    responses.value.splice(responses.value.length - 1, 1, {
      messageContent: content,
      isBeforeMessage: true,
      isAfterMessage: true,
    })
  }

  async function clearChat(apiHost?: string, clientId?: string) {
    // Clear on server if API host and client ID provided
    if (apiHost && clientId) {
      try {
        const username = getNetwatchUser()
        await fetch(`${apiHost}/clear-context`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-client-id': clientId,
            'x-username': username,
          },

        })
      }
      catch (error) {
        console.warn('Error clearing chat on server:', error)
      }
    }

    // Clear locally
    responses.value = [
      {
        isReply: undefined,
        messageContent:
          '👋 Hi there! I am here to help you monitor and manage your infrastructure. What would you like to know?',
        messageAvatar: undefined,
        aiInfo: true,
        isAfterMessage: false,
        isBeforeMessage: true,
        isLoadingMessage: false,
        time: new Date(),
      },
    ]
  }

  return {
    responses,
    isLoaded,
    loadMessages,
    updateResponses,
    deleteLastResponse,
    clearChat,
  }
})
